import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../lib/api.js'
import { auth } from '../lib/auth.js'
import { cart } from '../lib/cart.js'
import { aud } from '../lib/format.js'
import { DELIVERY_OPTIONS, GST_RATE, SITE } from '../data/site.js'
import Icon from '../components/Icon.jsx'

const STEPS = ['Your details', 'Delivery', 'Review & place order']

export default function Checkout() {
  const navigate = useNavigate()
  const [items, setItems] = useState(cart.get())
  const [settings, setSettings] = useState({})
  const [step, setStep] = useState(0)
  const [form, setForm] = useState(() => {
    const member = auth.customer()
    return {
      name: member?.name || '', phone: member?.phone || '', email: member?.email || '',
      delivery: 'pickup', address: member?.address || '', suburb: member?.suburb || '',
      state: 'VIC', postcode: member?.postcode || '', notes: '',
    }
  })
  const [errors, setErrors] = useState({})
  const [placing, setPlacing] = useState(false)
  const [apiError, setApiError] = useState('')
  const [payment, setPayment] = useState('card')
  const stripeEnabled = Boolean(settings.stripe_enabled)
  // Cash is handed over at the counter, so it only exists for warehouse pickup.
  const cashEnabled = settings.cash_enabled !== false && form.delivery === 'pickup'
  const pickupDays = Number(settings.cash_pickup_days) || 2

  useEffect(() => cart.subscribe(setItems), [])
  useEffect(() => {
    api.settings().then((d) => {
      const s = d.settings || {}
      setSettings(s)
      // Card is the default, but with Stripe off it isn't offered — leave the radio group
      // with nothing selected and the customer can't tell what they're about to be charged with.
      if (!s.stripe_enabled) setPayment((p) => (p === 'card' ? 'bank' : p))
    }).catch(() => {})
  }, [])

  const subtotal = useMemo(() => items.reduce((n, it) => n + it.unitPrice * it.qty, 0), [items])
  const deliveryOpt = DELIVERY_OPTIONS.find((o) => o.id === form.delivery) || DELIVERY_OPTIONS[0]
  const shipping = deliveryOpt.price(settings)
  const gst = (subtotal + shipping) * GST_RATE
  const total = subtotal + shipping + gst

  if (!items.length) {
    return (
      <main className="container page">
        <div className="empty-state">
          <p>Your cart is empty — nothing to check out yet.</p>
          <Link to="/shop" className="btn btn-primary">Browse products</Link>
        </div>
      </main>
    )
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  // Picking delivery after choosing cash would silently downgrade the order to a bank
  // transfer on the server; move the selection back to a method that works instead.
  const setDelivery = (id) => {
    setForm((f) => ({ ...f, delivery: id }))
    if (id !== 'pickup' && payment === 'cash') setPayment(stripeEnabled ? 'card' : 'bank')
  }

  function validateStep(i) {
    const errs = {}
    if (i === 0) {
      if (!form.name.trim()) errs.name = 'Please enter your name'
      if (!/^[\d\s+()-]{8,}$/.test(form.phone.trim())) errs.phone = 'Please enter a valid phone number'
      if (form.email && !/^\S+@\S+\.\S+$/.test(form.email.trim())) errs.email = 'Please enter a valid email'
    }
    if (i === 1 && form.delivery !== 'pickup') {
      if (!form.address.trim()) errs.address = 'Street address is required for delivery'
      if (!form.suburb.trim()) errs.suburb = 'Suburb is required'
      if (!/^[38]\d{3}$/.test(form.postcode.trim())) errs.postcode = 'Victorian postcode required (starts with 3)'
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function next() {
    if (validateStep(step)) setStep(step + 1)
  }

  async function placeOrder() {
    setPlacing(true)
    setApiError('')
    try {
      const res = await api.createOrder({
        customer: form,
        payment: payment === 'cash' && cashEnabled ? 'cash' : stripeEnabled ? payment : 'bank',
        items: items.map((it) => ({ id: it.id, qty: it.qty })),
      }, auth.token())
      cart.clear()
      if (res.stripeUrl) {
        window.location.href = res.stripeUrl
      } else {
        // cardFailed: they asked for card but Stripe wouldn't open — say so on the
        // order page instead of dropping them on a bank-transfer screen unexplained.
        navigate(`/order/${res.id}?t=${res.token}${res.cardFailed ? '&cardfail=1' : ''}`)
      }
    } catch (e) {
      setApiError(e.message)
      setPlacing(false)
    }
  }

  return (
    <main className="container page checkout-page">
      <header className="page-head"><h1>Checkout</h1></header>

      <ol className="checkout-steps" aria-label="Checkout progress">
        {STEPS.map((label, i) => (
          <li key={label} className={i === step ? 'current' : i < step ? 'done' : ''}>
            <span className="step-dot">{i < step ? <Icon name="check" size={13} /> : i + 1}</span>
            <span className="step-label">{label}</span>
          </li>
        ))}
      </ol>

      <div className="checkout-layout">
        <div className="checkout-main">
          {step === 0 && (
            <section className="panel">
              <h2>Your details</h2>
              <div className="field">
                <label htmlFor="co-name">Full name *</label>
                <input id="co-name" value={form.name} onChange={set('name')} autoComplete="name" />
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>
              <div className="field-row">
                <div className="field">
                  <label htmlFor="co-phone">Phone *</label>
                  <input id="co-phone" value={form.phone} onChange={set('phone')} inputMode="tel" autoComplete="tel" placeholder="04xx xxx xxx" />
                  {errors.phone && <span className="field-error">{errors.phone}</span>}
                </div>
                <div className="field">
                  <label htmlFor="co-email">Email (for your order confirmation)</label>
                  <input id="co-email" value={form.email} onChange={set('email')} inputMode="email" autoComplete="email" />
                  {errors.email && <span className="field-error">{errors.email}</span>}
                </div>
              </div>
              <button className="btn btn-primary btn-lg" onClick={next}>Continue to delivery <Icon name="arrowRight" size={16} /></button>
            </section>
          )}

          {step === 1 && (
            <section className="panel">
              <h2>Pickup or delivery</h2>
              <div className="delivery-options" role="radiogroup">
                {DELIVERY_OPTIONS.map((opt) => (
                  <label key={opt.id} className={`delivery-option ${form.delivery === opt.id ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="delivery"
                      value={opt.id}
                      checked={form.delivery === opt.id}
                      onChange={() => setDelivery(opt.id)}
                    />
                    <span className="delivery-option-body">
                      <strong>{opt.label}</strong>
                      <small>{opt.detail}</small>
                    </span>
                    <span className="delivery-option-price">{opt.price(settings) === 0 ? 'Free' : aud(opt.price(settings))}</span>
                  </label>
                ))}
              </div>

              {form.delivery !== 'pickup' && (
                <div className="delivery-address">
                  <p className="summary-note" style={{ marginTop: 0, marginBottom: 14 }}>
                    Delivery covers addresses within 50 km of our Ravenhall warehouse. If your address is
                    outside the zone, we'll contact you before processing the order.
                  </p>
                  <div className="field">
                    <label htmlFor="co-address">Street address *</label>
                    <input id="co-address" value={form.address} onChange={set('address')} autoComplete="street-address" />
                    {errors.address && <span className="field-error">{errors.address}</span>}
                  </div>
                  <div className="field-row">
                    <div className="field">
                      <label htmlFor="co-suburb">Suburb *</label>
                      <input id="co-suburb" value={form.suburb} onChange={set('suburb')} />
                      {errors.suburb && <span className="field-error">{errors.suburb}</span>}
                    </div>
                    <div className="field field-sm">
                      <label htmlFor="co-state">State</label>
                      <input id="co-state" value="VIC" disabled title="Delivery is available in Victoria only" />
                    </div>
                    <div className="field field-sm">
                      <label htmlFor="co-postcode">Postcode *</label>
                      <input id="co-postcode" value={form.postcode} onChange={set('postcode')} inputMode="numeric" maxLength={4} />
                      {errors.postcode && <span className="field-error">{errors.postcode}</span>}
                    </div>
                  </div>
                </div>
              )}

              <div className="field">
                <label htmlFor="co-notes">Order notes (optional)</label>
                <textarea id="co-notes" rows={3} value={form.notes} onChange={set('notes')} placeholder="Anything we should know — access, timing, custom size questions…" />
              </div>

              <div className="panel-actions">
                <button className="btn btn-outline" onClick={() => setStep(0)}>Back</button>
                <button className="btn btn-primary btn-lg" onClick={next}>Review order <Icon name="arrowRight" size={16} /></button>
              </div>
            </section>
          )}

          {step === 2 && (
            <section className="panel">
              <h2>Review & place order</h2>
              <div className="review-block">
                <h3>Contact</h3>
                <p>{form.name} · {form.phone}{form.email ? ` · ${form.email}` : ''}</p>
                <h3>{form.delivery === 'pickup' ? 'Pickup' : 'Delivery'}</h3>
                <p>
                  {form.delivery === 'pickup'
                    ? 'Warehouse pickup — 1/45 Ravenhall Way, Ravenhall VIC 3023 (we\'ll text you when it\'s ready)'
                    : `${form.address}, ${form.suburb} ${form.state} ${form.postcode}`}
                </p>
                {form.notes && (<><h3>Notes</h3><p>{form.notes}</p></>)}
              </div>
              <div className="review-items">
                {items.map((it) => (
                  <div key={it.id} className="review-item">
                    <span>{it.qty} × {it.name}{Object.keys(it.opts || {}).length ? ` (${Object.values(it.opts).join(', ')})` : ''}</span>
                    <strong>{aud(it.unitPrice * it.qty)}</strong>
                  </div>
                ))}
              </div>
              {stripeEnabled || cashEnabled ? (
                <div className="delivery-options" role="radiogroup" aria-label="Payment method">
                  {stripeEnabled && (
                    <label className={`delivery-option ${payment === 'card' ? 'selected' : ''}`}>
                      <input type="radio" name="payment" value="card" checked={payment === 'card'} onChange={() => setPayment('card')} />
                      <span className="delivery-option-body">
                        <strong>Card — Visa, Mastercard, Amex</strong>
                        <small>Secure checkout by Stripe · order confirmed instantly</small>
                      </span>
                      <Icon name="shield" size={19} />
                    </label>
                  )}
                  <label className={`delivery-option ${payment === 'bank' ? 'selected' : ''}`}>
                    <input type="radio" name="payment" value="bank" checked={payment === 'bank'} onChange={() => setPayment('bank')} />
                    <span className="delivery-option-body">
                      <strong>Direct bank transfer</strong>
                      <small>We'll show account details and a unique reference after checkout</small>
                    </span>
                    <Icon name="bank" size={19} />
                  </label>
                  {cashEnabled && (
                    <label className={`delivery-option ${payment === 'cash' ? 'selected' : ''}`}>
                      <input type="radio" name="payment" value="cash" checked={payment === 'cash'} onChange={() => setPayment('cash')} />
                      <span className="delivery-option-body">
                        <strong>Cash on pickup</strong>
                        <small>
                          Pay at our Ravenhall warehouse when you collect. We'll hold your order for{' '}
                          {pickupDays} business days — after that it may be released.
                        </small>
                      </span>
                      <Icon name="truck" size={19} />
                    </label>
                  )}
                </div>
              ) : (
                <div className="payment-note">
                  <Icon name="bank" size={20} />
                  <p>
                    <strong>Payment by bank transfer.</strong> After you place the order we'll show our account details
                    and a unique payment reference. Your order is processed as soon as the transfer is verified.
                  </p>
                </div>
              )}
              {payment === 'cash' && cashEnabled && (
                <div className="payment-note">
                  <Icon name="pin" size={20} />
                  <p>
                    <strong>Nothing to pay now.</strong> Bring the exact amount to {SITE.address}, open {SITE.hours}.
                    Your collect-by date is on the next page — please come in by then, or call {SITE.phone} if you need longer.
                  </p>
                </div>
              )}
              {apiError && <p className="field-error">{apiError}</p>}
              <div className="panel-actions">
                <button className="btn btn-outline" onClick={() => setStep(1)}>Back</button>
                <button className="btn btn-primary btn-lg" onClick={placeOrder} disabled={placing}>
                  {placing ? 'Placing order…'
                    : stripeEnabled && payment === 'card' ? `Pay by card — ${aud(total)}`
                    : payment === 'cash' && cashEnabled ? `Reserve for pickup — ${aud(total)} cash`
                    : `Place order — ${aud(total)}`}
                </button>
              </div>
            </section>
          )}
        </div>

        <aside className="cart-summary">
          <h2>Summary</h2>
          <div className="summary-items">
            {items.map((it) => (
              <div key={it.id} className="review-item">
                <span>{it.qty} × {it.name}</span>
                <strong>{aud(it.unitPrice * it.qty)}</strong>
              </div>
            ))}
          </div>
          <dl>
            <div><dt>Subtotal (ex GST)</dt><dd>{aud(subtotal)}</dd></div>
            <div><dt>Shipping</dt><dd>{shipping === 0 ? 'Free' : aud(shipping)}</dd></div>
            <div><dt>GST (10%)</dt><dd>{aud(gst)}</dd></div>
            <div className="total"><dt>Total (inc GST)</dt><dd>{aud(total)}</dd></div>
          </dl>
        </aside>
      </div>
    </main>
  )
}
